import { SortingItemsHelper } from '@modeso/aumico-lib-annualreport-fe';
import {
    AccountMappingTemplateGroupItem,
    IAccount,
    IAccountMappingSubFLI,
    IAccountMappingTemplate,
    IAccountMappingTemplateGroup,
    IAccountMappingTemplateGroupItemFLI,
    IAccountMappingTemplateGroupItemSubtotal,
    IFinancialStatementInterpretation,
    ItemType,
} from '@modeso/types__aumico-lib-annualreport-be';
import { FinancialLineItemType } from '@modeso/types__aumico-lib-settings-be';
import {
    IItemNameType,
    ITemplateEntityDTO,
    ITemplateGroupEntityDTO,
    ITemplateGroupItemEntityDTO,
} from '@modeso/types__aumico-lib-templates-be';

function mapToAccountMappingTemplateGroupItem(
    templateGroupItem: ITemplateGroupItemEntityDTO,
    reportLocale: string,
): AccountMappingTemplateGroupItem {
    const otherItems = {
        name: templateGroupItem.name,
        calculatedOpeningBalance: 0,
        calculatedClosingBalance: 0,
    };

    if (templateGroupItem.isSubtotal === true) {
        const accountMappingTemplateGroupItem: IAccountMappingTemplateGroupItemSubtotal = {
            type: ItemType.SUB_TOTAL,
            scope: templateGroupItem.scope,
            caption: templateGroupItem.description[reportLocale],
            ...otherItems,
        };
        return accountMappingTemplateGroupItem;
    } else {
        const accountMappingTemplateGroupItem: IAccountMappingTemplateGroupItemFLI = {
            type: ItemType.FLI,
            fliType: templateGroupItem.type,
            cashFlowType: templateGroupItem.fliObj.fliObj.cashflow,
            accounts: [],
            reclassesAccounts: [],
            caption: templateGroupItem.description[reportLocale],
            ...otherItems,
        };

        templateGroupItem.subFlisAndOfThatItems?.forEach((subFliOrOfThatItem) => {
            const subFli: IAccountMappingSubFLI = {
                type: subFliOrOfThatItem.isOfThatItem ? ItemType.OF_THAT_ITEM : ItemType.SUB_FLI,
                name: subFliOrOfThatItem.name,
                caption: subFliOrOfThatItem.caption[reportLocale],
                accounts: [],
                reclassesAccounts: [],
            };
            accountMappingTemplateGroupItem.accounts.push(subFli);
        });

        return accountMappingTemplateGroupItem;
    }
}

export function getFlipRule(
    baseSystemRules: Omit<IFinancialStatementInterpretation, 'numberFormat'>,
    systemRules: Omit<IFinancialStatementInterpretation, 'numberFormat'>,
    type: FinancialLineItemType,
): boolean {
    if (baseSystemRules[type] === systemRules[type]) {
        return false;
    } else {
        return true;
    }
}

function mapToAccountMappingTemplateGroup(
    templateGroup: ITemplateGroupEntityDTO,
    flipRules: IFinancialStatementInterpretation,
    reportLocale: string,
): IAccountMappingTemplateGroup {
    const accountMappingtemplateGroup: IAccountMappingTemplateGroup = {
        name: templateGroup.groupRef.name,
        title: templateGroup.groupRef.title[reportLocale],
        totalName: templateGroup.groupRef.totalName[reportLocale],
        hideTotal: templateGroup.groupRef.hideTotal,
        groups: templateGroup.groups.map((group) => mapToAccountMappingTemplateGroup(group, flipRules, reportLocale)),
        items: templateGroup.items.map((item) => mapToAccountMappingTemplateGroupItem(item, reportLocale)),
        calculatedOpeningBalance: 0,
        calculatedClosingBalance: 0,
        isHiddenGroup: templateGroup.groupRef.isHiddenGroup,
        sortedItems: templateGroup.sortedItems as IItemNameType[],
    };
    return accountMappingtemplateGroup;
}

export function mapToAccountMappingTemplate(
    template: ITemplateEntityDTO,
    flipRules: IFinancialStatementInterpretation,
    reportLocale: string,
): IAccountMappingTemplate {
    const accountMappingTemplate: IAccountMappingTemplate = {
        name: template.name,
        type: template.type,
        group: mapToAccountMappingTemplateGroup(template.group, flipRules, reportLocale),
        accountBasis: template.accountBasis,
        legalForm: template.legalForm,
        profitOfTheYear: template.profitOfTheYear,
        equityGroupName: template.equityGroupName,
        uncoveredShortfallAssetsFli: template.uncoveredShortfallAssetsFli,
        uncoveredShortfallLiabilitiesFli: template.uncoveredShortfallLiabilitiesFli,
    };
    accountMappingTemplate.group = SortingItemsHelper.mapGroupSortedItemsToSortedMappingItem(
        accountMappingTemplate.group,
    );
    return accountMappingTemplate;
}

export function getAccountInGroup(
    currentAccount: IAccount,
    previousYearFlisAndSubflis: (IAccountMappingSubFLI | IAccountMappingTemplateGroupItemFLI)[],
): IAccount | undefined {
    for (const item of previousYearFlisAndSubflis) {
        if (item.type === ItemType.FLI) {
            const account = item.accounts.find(
                (account) =>
                    account.type !== ItemType.SUB_FLI &&
                    (account as IAccount).accountNumber === currentAccount.accountNumber,
            );
            if (account != null) {
                return account as IAccount;
            }
            continue;
        }

        if (item.type === ItemType.SUB_FLI) {
            const account = item.accounts.find((account) => account.accountNumber === currentAccount.accountNumber);
            if (account != null) {
                return account;
            }
        }
    }

    return undefined;
}
